import { gatewayApi as api } from './gatewayApi';

// Interfaces
export interface NCSubtype {
  Id?: number;
  Name: string;
  Description?: string;
  Active: boolean;
  NCTypeId: number;
  NCType?: {
    Id: number;
    Name: string;
    Description?: string;
    Active: boolean;
  };
  CreatedDate?: string;
  ModifiedDate?: string;
}

export interface NCType {
  Id: number;
  Name: string;
  Description?: string;
  Active: boolean;
}

export interface NCSubtypeListResponse {
  data: NCSubtype[];
  total: number;
}

export interface NCSubtypeSearchParams {
  page?: number;
  pageSize?: number;
  search?: string;
  active?: boolean;
  ncTypeId?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

function ensureArray<T>(value: any): T[] {
  if (Array.isArray(value)) return value as T[];
  if (value && Array.isArray(value.data)) return value.data as T[];
  return [] as T[];
}

// API Service
export const ncsubtypeApi = {
  // CRUD Operations
  getAll: async (params?: NCSubtypeSearchParams): Promise<NCSubtypeListResponse> => {
    const res = await api.get<any>('/ncsubtype', params);
    if (res && Array.isArray(res.data) && typeof res.total === 'number') {
      return res as NCSubtypeListResponse;
    }
    const items = ensureArray<NCSubtype>(res);
    return { data: items, total: items.length };
  },

  getById: (id: number): Promise<NCSubtype> =>
    api.get(`/ncsubtype/${id}`),

  create: (ncsubtype: Omit<NCSubtype, 'Id'>): Promise<NCSubtype> =>
    api.post('/ncsubtype', ncsubtype),

  update: (id: number, ncsubtype: Partial<NCSubtype>): Promise<NCSubtype> =>
    api.put(`/ncsubtype/${id}`, ncsubtype),

  delete: (id: number): Promise<{ success: boolean; message: string }> =>
    api.delete(`/ncsubtype/${id}`),

  // Search and Filter
  search: async (params: NCSubtypeSearchParams): Promise<NCSubtypeListResponse> => {
    const res = await api.post<any>('/ncsubtype/search', params);
    if (res && Array.isArray(res.data) && typeof res.total === 'number') {
      return res as NCSubtypeListResponse;
    }
    const items = ensureArray<NCSubtype>(res);
    return { data: items, total: items.length };
  },

  // NCType Operations
  getNCTypes: async (): Promise<NCType[]> => {
    const res = await api.get<any>('/nctype');
    return ensureArray<NCType>(res);
  },

  getByNCTypeId: async (ncTypeId: number): Promise<NCSubtype[]> => {
    const res = await api.get<any>(`/ncsubtype/by-nctype/${ncTypeId}`);
    return ensureArray<NCSubtype>(res);
  },

  // Bulk Operations
  bulkActivate: (ids: number[]): Promise<{ success: boolean; message: string }> =>
    api.put('/ncsubtype/bulk-activate', { ids }),

  bulkDeactivate: (ids: number[]): Promise<{ success: boolean; message: string }> =>
    api.put('/ncsubtype/bulk-deactivate', { ids }),

  bulkDelete: (ids: number[]): Promise<{ success: boolean; message: string }> =>
    api.post('/ncsubtype/bulk-delete', { ids }),

  // Statistics
  getStatistics: (): Promise<{
    total: number;
    active: number;
    inactive: number;
    byNCType: { [key: number]: number };
  }> =>
    api.get('/ncsubtype/statistics'),

  // Export
  exportToCSV: (params?: NCSubtypeSearchParams): Promise<Blob> =>
    api.getBlob('/ncsubtype/export', { params }),

  // Validation
  validation: {
    validateNCSubtype: (ncsubtype: Partial<NCSubtype>): string[] => {
      const errors: string[] = [];

      // Name validation
      if (!ncsubtype.Name || ncsubtype.Name.trim().length === 0) {
        errors.push('Subtype Name is required');
      } else if (ncsubtype.Name.trim().length < 2) {
        errors.push('Subtype Name must be at least 2 characters long');
      } else if (ncsubtype.Name.trim().length > 100) {
        errors.push('Subtype Name must be less than 100 characters');
      }

      // NCType validation
      if (!ncsubtype.NCTypeId) {
        errors.push('NC Type is required');
      }

      // Description validation (optional but if provided, validate length)
      if (ncsubtype.Description && ncsubtype.Description.trim().length > 500) {
        errors.push('Description must be less than 500 characters');
      }

      // Active validation
      if (ncsubtype.Active === undefined || ncsubtype.Active === null) {
        errors.push('Active status is required');
      }

      return errors;
    },

    validateBulkOperation: (ids: number[]): string[] => {
      const errors: string[] = [];

      if (!ids || ids.length === 0) {
        errors.push('At least one NC Subtype must be selected');
      }

      if (ids.length > 100) {
        errors.push('Cannot perform bulk operation on more than 100 items at once');
      }

      return errors;
    }
  }
};

export default ncsubtypeApi; 